import React, { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Label,
  Checkbox,
  Spinner,
  MessageBar,
  MessageBarBody,
  Text,
} from '@fluentui/react-components';
import {
  Mail24Regular,
  Send24Regular,
  Save24Regular,
  Dismiss24Regular,
  Warning16Regular
} from '@fluentui/react-icons';
import { alertsService } from '../../services/alertsService';
import type { UpdateSmtpConfigRequest } from '../../types';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '600px',
    width: '90vw',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  description: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    marginBottom: '16px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  fullWidth: {
    gridColumn: '1 / -1',
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  checkboxRow: {
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    marginTop: '8px',
  },
  warningBox: {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: '4px',
    padding: '12px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  warningIcon: {
    color: tokens.colorPaletteYellowForeground1,
    flexShrink: 0,
  },
  warningText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  testSection: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  testRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end',
  },
  testInput: {
    flex: 1,
  },
  successText: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: '8px',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: '8px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
  },
});

interface SmtpConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

export const SmtpConfigModal: React.FC<SmtpConfigModalProps> = ({
  open,
  onClose,
  onSaved
}) => {
  const styles = useStyles();

  // Form state
  const [server, setServer] = useState('');
  const [port, setPort] = useState(587);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [useSsl, setUseSsl] = useState(false);
  const [useStartTls, setUseStartTls] = useState(true);
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [autoPromoteHours, setAutoPromoteHours] = useState(4);

  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasPassword, setHasPassword] = useState(false);

  useEffect(() => {
    if (open) {
      loadConfig();
    }
  }, [open]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      setTestResult(null);

      const config = await alertsService.getSmtpConfig();

      setServer(config.server || '');
      setPort(config.port || 587);
      setUsername(config.username || '');
      setPassword('');
      setUseSsl(config.useSsl);
      setUseStartTls(config.useStartTls);
      setFromEmail(config.fromEmail || '');
      setFromName(config.fromName || '');
      setIsEnabled(config.isEnabled);
      setHasPassword(config.hasPassword);
      setAutoPromoteHours(config.autoPromoteToCriticalHours ?? 4);
    } catch (err) {
      console.error('Error loading SMTP config:', err);
      setError('Erro ao carregar configuração SMTP');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const request: UpdateSmtpConfigRequest = {
        server,
        port,
        username,
        password: password || undefined,
        useSsl,
        useStartTls,
        fromEmail,
        fromName,
        isEnabled,
        autoPromoteToCriticalHours: autoPromoteHours
      };

      await alertsService.updateSmtpConfig(request);

      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error saving SMTP config:', err);
      setError('Erro ao salvar configuração SMTP');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setTestResult({ success: false, message: 'Informe um email para teste' });
      return;
    }

    try {
      setTesting(true);
      setTestResult(null);

      const result = await alertsService.testSmtpConfig({ recipientEmail: testEmail });

      setTestResult({
        success: result.success,
        message: result.success ? result.message : `${result.message}: ${result.errorDetails || ''}`
      });
    } catch (err) {
      console.error('Error sending test email:', err);
      setTestResult({ success: false, message: 'Erro ao enviar email de teste' });
    } finally {
      setTesting(false);
    }
  };

  const handleClose = () => {
    if (!saving && !testing) {
      onClose();
    }
  };

  return (
    <Dialog open={open} modalType="modal" onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface className={styles.dialogSurface}>
        <DialogBody>
          <DialogTitle>
            <div className={styles.header}>
              <Mail24Regular />
              Configuração do Servidor SMTP
            </div>
          </DialogTitle>

          <DialogContent>
            {loading ? (
              <Spinner size="medium" label="Carregando..." />
            ) : (
              <>
                {/* Warning for Office365 */}
                <div className={styles.warningBox}>
                  <Warning16Regular className={styles.warningIcon} />
                  <div className={styles.warningText}>
                    <strong>Office365 / Outlook.com:</strong> Se usar Office365, você precisa de uma <strong>App Password (Senha de Aplicativo)</strong>, não a senha normal da conta.
                  </div>
                </div>

                {error && (
                  <MessageBar intent="error" style={{ marginBottom: '16px' }}>
                    <MessageBarBody>{error}</MessageBarBody>
                  </MessageBar>
                )}

                <div className={styles.formGrid}>
                  <div className={styles.fieldContainer}>
                    <Label required>Servidor SMTP</Label>
                    <Input
                      value={server}
                      onChange={(_, data) => setServer(data.value)}
                      placeholder="smtp.office365.com"
                    />
                  </div>

                  <div className={styles.fieldContainer}>
                    <Label required>Porta</Label>
                    <Input
                      type="number"
                      value={port.toString()}
                      onChange={(_, data) => setPort(parseInt(data.value) || 587)}
                      placeholder="587"
                    />
                  </div>

                  <div className={styles.fieldContainer}>
                    <Label required>Usuário</Label>
                    <Input
                      value={username}
                      onChange={(_, data) => setUsername(data.value)}
                      placeholder="usuario@dominio.com"
                    />
                  </div>

                  <div className={styles.fieldContainer}>
                    <Label>Senha / App Password</Label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(_, data) => setPassword(data.value)}
                      placeholder={hasPassword ? 'Deixe vazio para manter atual' : 'Digite a senha'}
                    />
                    {hasPassword && (
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        Deixe vazio para manter a senha atual
                      </Text>
                    )}
                  </div>

                  <div className={`${styles.fieldContainer} ${styles.fullWidth}`}>
                    <div className={styles.checkboxRow}>
                      <Checkbox
                        checked={useSsl}
                        onChange={(_, data) => {
                          setUseSsl(!!data.checked);
                          if (data.checked) setUseStartTls(false);
                        }}
                        label="Usar SSL/TLS (porta 465)"
                      />
                      <Checkbox
                        checked={useStartTls}
                        onChange={(_, data) => {
                          setUseStartTls(!!data.checked);
                          if (data.checked) setUseSsl(false);
                        }}
                        label="Usar STARTTLS (porta 587)"
                      />
                    </div>
                  </div>

                  <div className={styles.fieldContainer}>
                    <Label required>Email Remetente (FROM)</Label>
                    <Input
                      type="email"
                      value={fromEmail}
                      onChange={(_, data) => setFromEmail(data.value)}
                      placeholder="alertas@dominio.com"
                    />
                  </div>

                  <div className={styles.fieldContainer}>
                    <Label>Nome do Remetente</Label>
                    <Input
                      value={fromName}
                      onChange={(_, data) => setFromName(data.value)}
                      placeholder="Sistema de Alertas"
                    />
                  </div>

                  <div className={`${styles.fieldContainer} ${styles.fullWidth}`}>
                    <Checkbox
                      checked={isEnabled}
                      onChange={(_, data) => setIsEnabled(!!data.checked)}
                      label="Habilitar envio de alertas por email"
                    />
                  </div>

                  <div className={`${styles.fieldContainer} ${styles.fullWidth}`} style={{ paddingTop: '8px', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
                    <Label>Promover Warning para Critical após (horas)</Label>
                    <Input
                      type="number"
                      value={autoPromoteHours.toString()}
                      onChange={(_, data) => {
                        const v = parseInt(data.value);
                        setAutoPromoteHours(isNaN(v) ? 0 : Math.max(0, Math.min(168, v)));
                      }}
                      placeholder="4"
                      min={0}
                      max={168}
                    />
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                      Um Warning ativo por mais que este número de horas é promovido a Critical automaticamente. Use <strong>0</strong> para desligar a auto-promoção. Default: <strong>4 horas</strong>.
                    </Text>
                  </div>
                </div>

                {/* Test Section */}
                <div className={styles.testSection}>
                  <Label>Enviar Email de Teste</Label>
                  <div className={styles.testRow}>
                    <Input
                      className={styles.testInput}
                      type="email"
                      value={testEmail}
                      onChange={(_, data) => setTestEmail(data.value)}
                      placeholder="email@teste.com"
                    />
                    <Button
                      appearance="secondary"
                      icon={<Send24Regular />}
                      onClick={handleTestEmail}
                      disabled={testing || !server || !username}
                    >
                      {testing ? <Spinner size="tiny" /> : 'Enviar Teste'}
                    </Button>
                  </div>
                  {testResult && (
                    <Text className={testResult.success ? styles.successText : styles.errorText}>
                      {testResult.message}
                    </Text>
                  )}
                </div>
              </>
            )}
          </DialogContent>

          <DialogActions>
            <div className={styles.actions}>
              <Button
                appearance="secondary"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
                disabled={saving || testing}
              >
                Cancelar
              </Button>
              <Button
                appearance="primary"
                icon={<Save24Regular />}
                onClick={handleSave}
                disabled={loading || saving || !server || !username || !fromEmail}
              >
                {saving ? <Spinner size="tiny" /> : 'Salvar Configuração'}
              </Button>
            </div>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
